import {Component, EventEmitter, inject, Input, Output} from '@angular/core';
import {Table} from '@core/components';
import {RiskDeclarationRow, SurveyQuestion, SurveyRow} from '@core/types';
import {ModalService, SurveyService} from '@core/services';
import {Confirm} from '@core/modals';
import {AddDeclaration} from '@penal/modals/add-declaration/add-declaration';

@Component({
  selector: 'core-plan-resend',
  imports: [
    Table
  ],
  templateUrl: './plan-resend.html',
  styleUrl: './plan-resend.scss',
  standalone: true,
  host: {
    style: 'width: 100%;display:flex;flex-direction:column;gap:2rem'
  }
})
export class PlanResend {
  @Output() submit = new EventEmitter();
  @Output() phaseDataChanged = new EventEmitter<'surveys' | 'declarations'>();
  @Output() phaseOneLocked = new EventEmitter<void>();
  @Output() phaseOneSurveysLaunched = new EventEmitter<Array<{surveyId: string | number; dispatchKey: string}>>();
  @Output() phaseOneDeclarationUpdated = new EventEmitter<string | number>();
  @Input() set surveys(value: SurveyRow[]) {
    this._surveys = value || [];
    this.refreshLaunchedSurveyState();
  }
  get surveys(): SurveyRow[] {
    return this._surveys;
  }
  @Input() lockSelection = false;
  @Input() planId: string | number | null = null;
  @Input() declarations: RiskDeclarationRow[] = [];
  private modal = inject(ModalService);
  private surveyService = inject(SurveyService);
  private _surveys: SurveyRow[] = [];
  selectedSurveyIds = new Set<string>();
  launchedSurveyIds = new Set<string>();
  hasLaunchedInPhase = false;
  isLaunching = false;
  isSubmittingPhase = false;
  launchError = '';

  async onSubmit() {
    if (this.isSubmittingPhase) return;
    this.isSubmittingPhase = true;
    const confirmed = await this.askForConfirmation(
      'AVISO - SALIR DE FASE 1',
      'Al continuar a la Fase 2 ya no podrás reenviar más encuestas ni modificar declaraciones en este cierre. ¿Continuar?',
      'Continuar'
    );
    if (!confirmed) {
      this.isSubmittingPhase = false;
      return;
    }
    this.submit.emit();
    this.isSubmittingPhase = false;
  }

  openEditDeclaration(item: RiskDeclarationRow): void {
    const ref = this.modal.open<AddDeclaration, boolean>(AddDeclaration, {
      inputs: {
        declaration: item,
        phaseOneConfirmation: true,
      },
      outputs: {},
      options: {size: 'md', closeOnBackdrop: false},
    });

    ref.afterClosed$.subscribe(result => {
      if (result) {
        this.phaseOneDeclarationUpdated.emit(item.id);
        this.phaseDataChanged.emit('declarations');
      }
    });
  }

  toggleSurveySelection(surveyId: string | number, checked: boolean) {
    const key = String(surveyId);
    if (this.isSurveyLocked(surveyId)) {
      this.selectedSurveyIds.delete(key);
      return;
    }
    if (checked) {
      this.selectedSurveyIds.add(key);
      return;
    }
    this.selectedSurveyIds.delete(key);
  }

  isSurveySelected(surveyId: string | number): boolean {
    return this.selectedSurveyIds.has(String(surveyId));
  }

  isSurveyLocked(surveyId: string | number): boolean {
    return this.lockSelection || this.hasLaunchedInPhase || this.launchedSurveyIds.has(String(surveyId));
  }

  get canLaunchSurveys(): boolean {
    return !this.isLaunching && !this.lockSelection && !this.hasLaunchedInPhase && this.selectedSurveyIds.size > 0;
  }

  async launchSelectedSurveys() {
    if (!this.canLaunchSurveys) return;
    this.launchError = '';
    const selected = this.surveys.filter(item => this.selectedSurveyIds.has(String(item.id)));
    const totalEmployees = selected.reduce((acc, item) => acc + Number(item.employeesCount || 0), 0);
    const confirmed = await this.askForConfirmation(
      'AVISO - REENVÍO DE ENCUESTAS',
      `Vas a reenviar ${selected.length} encuestas a ${totalEmployees} empleados. Una vez lanzadas no podrás cancelar el envío. Podrás continuar a la Fase 2 cuando quieras y los resultados se irán recogiendo.`,
      'Lanzar'
    );
    if (!confirmed) return;

    this.isLaunching = true;
    let changed = false;
    let failed = 0;
    let skippedWithoutAssignments = 0;
    const launchedDispatches: Array<{surveyId: string | number; dispatchKey: string}> = [];
    for (const survey of selected) {
      const employeeIds = Array.from(new Set(
        (survey.assignments || [])
          .map(assignment => assignment.employeeId)
          .filter((id): id is string | number => id !== undefined && id !== null && id !== '')
      ));
      if (!employeeIds.length) {
        skippedWithoutAssignments++;
        continue;
      }

      const dispatchKey = `plan_${String(this.planId || 'na')}_survey_${String(survey.id)}_${Date.now()}`;
      const dispatchResponse = await this.surveyService.assignSurvey(
        survey.id,
        {
          employeeIds,
          forceNewDispatch: true,
          dispatchKey,
        },
        {skipLoader: true}
      );

      if (!dispatchResponse.success) {
        failed++;
        continue;
      }

      this.launchedSurveyIds.add(String(survey.id));
      launchedDispatches.push({surveyId: survey.id, dispatchKey});

      if (survey.status !== 'published') {
        await this.surveyService.updateSurvey(survey.id, {status: 'published'}, {skipLoader: true});
      }
      changed = true;
    }
    this.isLaunching = false;

    if (failed > 0) {
      this.launchError = 'No se pudo completar el reenvío en todas las encuestas seleccionadas.';
    } else if (skippedWithoutAssignments > 0 && !changed) {
      this.launchError = 'Las encuestas seleccionadas no tienen empleados asignados para reenviar.';
    } else if (skippedWithoutAssignments > 0) {
      this.launchError = 'Algunas encuestas no tenían empleados asignados y se omitieron.';
    }

    if (changed) {
      this.hasLaunchedInPhase = true;
      this.selectedSurveyIds.clear();
      this.phaseOneLocked.emit();
      this.phaseOneSurveysLaunched.emit(launchedDispatches);
      this.phaseDataChanged.emit('surveys');
    }
  }

  get isLaunchDisabled(): boolean {
    return !this.canLaunchSurveys;
  }

  get launchButtonText(): string {
    if (this.isLaunching) {
      return 'Lanzando...';
    }
    if (this.lockSelection || this.hasLaunchedInPhase) {
      return 'Encuestas lanzadas';
    }
    return 'Lanzar encuestas';
  }

  isSurveyLaunched(surveyId: string | number): boolean {
    return this.launchedSurveyIds.has(String(surveyId));
  }

  typeLabel(item: RiskDeclarationRow): string {
    return item.type === 'numeric' ? 'Numérica' : 'Booleana';
  }

  valueLabel(item: RiskDeclarationRow): string {
    const declaredValue = String(item.declaredValue ?? '').trim();
    if (declaredValue) return declaredValue;
    if (item.type === 'numeric') {
      const value = String(item.threshold ?? '').trim();
      return value === '' ? 'Sin valor' : value;
    }
    return item.booleanAnswer === 'no' ? 'No' : 'Sí';
  }

  surveyQuestions(item: SurveyRow): SurveyQuestion[] {
    return item.questions || [];
  }

  questionTypeLabel(item: SurveyQuestion): string {
    const type = String(item?.type || '').trim();
    const labels: Record<string, string> = {
      boolean: 'Booleana',
      numeric: 'Numérica',
      scale: 'Puntuación',
      score: 'Puntuación',
      enumerated: 'Selección única',
      single_choice: 'Selección única',
      multiple_choice: 'Selección múltiple',
      text: 'Texto libre',
    };

    return labels[type] || 'Sin tipo';
  }

  questionVariableLabels(item: SurveyQuestion): string[] {
    if (!Array.isArray(item?.questionVariables)) {
      return [];
    }

    return item.questionVariables
      .map((questionVariable: any) => String(questionVariable?.variableName || '').trim())
      .filter((label: string) => label !== '');
  }

  questionConceptLabels(item: SurveyQuestion): string[] {
    if (!Array.isArray(item?.questionVariables)) {
      return [];
    }

    return Array.from(new Set(
      item.questionVariables
        .flatMap((questionVariable: any) => {
          if (Array.isArray(questionVariable?.conceptNames)) {
            return questionVariable.conceptNames
              .map((value: any) => String(value || '').trim())
              .filter((label: string) => label !== '');
          }

          const legacyLabel = String((questionVariable as any)?.conceptName || '').trim();
          return legacyLabel ? [legacyLabel] : [];
        })
    ));
  }

  private askForConfirmation(title: string, description: string, confirmText: string): Promise<boolean> {
    return new Promise((resolve) => {
      this.modal.open<Confirm, boolean>(Confirm, {
        inputs: {
          title,
          description,
          confirmText,
          cancelText: 'Cancelar',
        },
        outputs: {
          close: () => resolve(false),
          confirm: () => resolve(true),
        },
        options: { size: 'md', closeOnBackdrop: true },
      });
    });
  }

  private refreshLaunchedSurveyState(): void {
    const launched = new Set<string>();
    for (const survey of this._surveys) {
      const key = String(survey.id);
      const hasDispatchAssignments = (survey.assignments || []).some(assignment => {
        const dispatchKey = String(assignment.dispatchKey || '').trim();
        return dispatchKey !== '';
      });
      if (hasDispatchAssignments) {
        launched.add(key);
      }
    }
    this.launchedSurveyIds = launched;
    this.hasLaunchedInPhase = launched.size > 0;
    if (this.hasLaunchedInPhase) {
      this.selectedSurveyIds.clear();
    }
  }
}
